/**
 * Streaming audio player for AIVOX listener PWA.
 *
 * Backend sends per-utterance audio chunks as base64-encoded MP3 (or PCM)
 * via the same fan-out events the iOS listener uses. We queue them and
 * play sequentially via Web Audio API. AudioContext is created lazily
 * inside the user-gesture handler to satisfy mobile autoplay policies.
 */

export class StreamingAudioPlayer {
  private ctx: AudioContext | null = null;
  private nextStartAt = 0;
  private queue: ArrayBuffer[] = [];
  private decoding = false;
  private gain: GainNode | null = null;

  /** Must be called from a user-gesture handler. */
  start(): void {
    if (this.ctx) return;
    const Ctor = (window as any).AudioContext || (window as any).webkitAudioContext;
    const ctx: AudioContext = new Ctor();
    const gain = ctx.createGain();
    gain.gain.value = 1.0;
    gain.connect(ctx.destination);
    this.ctx = ctx;
    this.gain = gain;
    this.nextStartAt = ctx.currentTime;
  }

  setVolume(v: number): void {
    if (this.gain) this.gain.gain.value = Math.max(0, Math.min(1, v));
  }

  enqueueBase64(b64: string): void {
    if (!this.ctx) return;
    const bytes = base64ToArrayBuffer(b64);
    this.queue.push(bytes);
    void this.drain();
  }

  private async drain(): Promise<void> {
    if (this.decoding || !this.ctx) return;
    this.decoding = true;
    try {
      while (this.queue.length > 0 && this.ctx) {
        const buf = this.queue.shift()!;
        let decoded: AudioBuffer;
        try {
          decoded = await this.ctx.decodeAudioData(buf.slice(0));
        } catch {
          // skip undecodable chunk
          continue;
        }
        const src = this.ctx.createBufferSource();
        src.buffer = decoded;
        src.connect(this.gain ?? this.ctx.destination);
        const now = this.ctx.currentTime;
        // Avoid drifting in the past; clamp start at currentTime+small epsilon.
        const startAt = Math.max(now + 0.02, this.nextStartAt);
        src.start(startAt);
        this.nextStartAt = startAt + decoded.duration;
      }
    } finally {
      this.decoding = false;
    }
  }

  stop(): void {
    this.queue = [];
    if (this.ctx) {
      void this.ctx.close();
      this.ctx = null;
      this.gain = null;
    }
  }
}

function base64ToArrayBuffer(b64: string): ArrayBuffer {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
}
